import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Button, Form, Row, Col, Badge, Alert, Spinner, Table, ButtonGroup } from 'react-bootstrap';
import { FaArrowLeft, FaPaperPlane } from 'react-icons/fa';
import api from '../../api/axios';
import { formatDateTime } from '../../utils/format';
import SupportThread from '../../components/support/SupportThread';
import { CATEGORIES, STATUSES, categoryMeta, statusMeta } from '../../components/support/supportMeta';
import '../../components/support/support.css';

const PAGE_SIZE = 25;

// 슈퍼관리자 고객센터 — 전체 문의 큐 + 답변.
// 정렬은 서버가 admin_unread DESC 로 내려준다(= 공이 우리에게 넘어온 것이 위).
const SupportAdminPage = () => {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({ status: '', category: '' });
    const [detail, setDetail] = useState(null);
    const [reply, setReply] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    // 수동 가리기 — 운영자가 본문에서 드래그한 범위. { messageId, start, end, text }
    const [sel, setSel] = useState(null);
    // 자동 가리기 확인 단계. 되돌릴 수 없으니 한 번 되묻는다.
    const [confirmAuto, setConfirmAuto] = useState(false);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
            if (filters.status) params.set('status', filters.status);
            if (filters.category) params.set('category', filters.category);
            const res = await api.get(`/superadmin/support/tickets?${params.toString()}`);
            setItems(res.data.data?.items || []);
            setTotal(res.data.data?.total || 0);
        } catch (err) {
            setError(err.response?.data?.error || '문의 목록을 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    }, [page, filters]);

    useEffect(() => { if (!detail) fetchList(); }, [fetchList, detail]);

    const openDetail = async (id) => {
        setError('');
        setLoading(true);
        try {
            const res = await api.get(`/superadmin/support/tickets/${id}`);
            setDetail(res.data.data);
            setReply('');
        } catch (err) {
            setError(err.response?.data?.error || '문의를 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!reply.trim()) return;
        setSaving(true);
        setError('');
        try {
            await api.post(`/superadmin/support/tickets/${detail.ticket.id}/messages`, { body: reply.trim() });
            setReply('');
            await openDetail(detail.ticket.id);
        } catch (err) {
            setError(err.response?.data?.error || '답변 등록에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const changeStatus = async (status) => {
        setSaving(true);
        setError('');
        try {
            await api.put(`/superadmin/support/tickets/${detail.ticket.id}/status`, { status });
            await openDetail(detail.ticket.id);
        } catch (err) {
            setError(err.response?.data?.error || '상태 변경에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    // 분류는 단순 라벨이 아니라 **보존 기간**이다 — 파기 시 결제·구독만 남고 나머지는
    // 티켓째 지워진다. 그래서 양방향으로 쓴다: 결제 분쟁인데 '기타'로 들어온 걸 살리고,
    // 반대로 결제 칸에 손님 연락처를 적어놓은 걸 옮겨서 파기 때 지워지게 한다.
    const changeCategory = async (category) => {
        if (category === detail.ticket.category) return;
        setSaving(true);
        setError('');
        try {
            await api.put(`/superadmin/support/tickets/${detail.ticket.id}/category`, { category });
            await openDetail(detail.ticket.id);
        } catch (err) {
            setError(err.response?.data?.error || '분류 변경에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    // ── 가리기(redaction) ──
    //
    // 왜 지우지 않고 가리는가: 티켓을 지우면 결제 분쟁의 증거가 같이 사라지고, 민사에서
    // 문서 멸실은 '없어진 내용이 지운 쪽에 불리했을 것' 으로 추정되는 방향으로 작동한다.
    // 가림은 가려진 흔적이 본문에 남아서(■) 법정 문서의 redaction 과 같은 성질이 된다.
    // 되돌릴 수 없으므로 양쪽 다 확인 단계를 둔다.

    // ① 자동 — 번호 패턴을 티켓 전체에서 일괄 치환. 1초에 끝나는 게 핵심이다.
    const redactAuto = async () => {
        if (!confirmAuto) { setConfirmAuto(true); return; }
        setSaving(true);
        setError('');
        try {
            const res = await api.put(`/superadmin/support/tickets/${detail.ticket.id}/redact`);
            setConfirmAuto(false);
            setSel(null);
            await openDetail(detail.ticket.id);
            if (!res.data.data?.messages && !res.data.data?.title) {
                // 패턴이 없으면 한글 표기일 수 있다 — 수동으로 가야 한다는 걸 알려준다.
                setError('번호 패턴이 없습니다. 한글로 적힌 번호라면 본문에서 직접 선택해 가려주세요.');
            }
        } catch (err) {
            setError(err.response?.data?.error || '가리기에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    // ② 수동 — 본문에서 드래그한 범위를 집는다. 정규식이 못 잡는 것(한글 표기, 이름,
    // 주소)을 사람이 판단해서 덮는 자리다.
    //
    // 오프셋은 **선택이 그 메시지 본문 하나 안에서 끝난 경우만** 받는다. 두 메시지에
    // 걸쳐 드래그하면 anchor/focus 가 다른 노드라 좌표의 의미가 사라진다.
    // 본문이 단일 텍스트 노드로 렌더되는 데 기대고 있다(SupportThread 주석 참조).
    const pickSelection = (m) => {
        const s = window.getSelection();
        if (!s || s.isCollapsed || s.rangeCount === 0) { setSel(null); return; }
        const r = s.getRangeAt(0);
        if (r.startContainer !== r.endContainer || r.startContainer.nodeType !== Node.TEXT_NODE) {
            setSel(null);
            setError('한 메시지 안에서 가릴 부분만 선택해주세요.');
            return;
        }
        const start = Math.min(r.startOffset, r.endOffset);
        const end = Math.max(r.startOffset, r.endOffset);
        if (end <= start) { setSel(null); return; }
        setError('');
        setSel({ messageId: m.id, start, end, text: String(m.body).slice(start, end) });
    };

    const redactSelection = async () => {
        if (!sel) return;
        setSaving(true);
        setError('');
        try {
            await api.put(`/superadmin/support/messages/${sel.messageId}/redact`,
                { start: sel.start, end: sel.end });
            setSel(null);
            await openDetail(detail.ticket.id);
        } catch (err) {
            setError(err.response?.data?.error || '가리기에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    // ── 스레드 ──
    if (detail) {
        const { ticket, messages } = detail;
        const cat = categoryMeta(ticket.category);
        const st = statusMeta(ticket.status);
        return (
            <Container className="py-4" style={{ maxWidth: 900 }}>
                <Button variant="link" className="ps-0 mb-2" onClick={() => { setDetail(null); setError(''); setSel(null); setConfirmAuto(false); }}>
                    <FaArrowLeft className="me-1" /> 목록으로
                </Button>
                <Card>
                    <Card.Header>
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                            <Badge bg={cat.variant}>{cat.label}</Badge>
                            <Badge bg={st.variant}>{st.label}</Badge>
                            <span className="sp-meta ms-auto">{formatDateTime(ticket.created_at)}</span>
                        </div>
                        <div className="sp-list-title">{ticket.title}</div>
                        <div className="sp-meta">
                            {ticket.shopName || `매장 #${ticket.shop_id}`}
                            {' · '}
                            {ticket.author_name || ticket.author_username || '-'}
                            {' · 매장ID '}{ticket.shop_id}
                        </div>
                    </Card.Header>
                    <Card.Body>
                        {error && <Alert variant="danger">{error}</Alert>}
                        <SupportThread messages={messages} mySide="admin" onSelect={pickSelection} />
                    </Card.Body>
                    <Card.Footer>
                        {/* 답변하려면 어차피 본문을 읽는다 — 그 자리에서 분류를 고칠 수 있게
                            스레드 안에 둔다. 아래 설명이 보존 결과를 같이 알려주는 게 핵심이다.
                            360px 에서 라벨+셀렉트가 한 줄, 설명이 다음 줄로 나뉜다. */}
                        <div className="mb-3">
                            <div className="d-flex align-items-center gap-2">
                                <Form.Label htmlFor="sp-admin-cat" className="sp-meta mb-0 text-nowrap">
                                    분류
                                </Form.Label>
                                <Form.Select id="sp-admin-cat" size="sm" value={ticket.category}
                                    disabled={saving} style={{ maxWidth: 200 }}
                                    onChange={e => changeCategory(e.target.value)}>
                                    {CATEGORIES.map(c => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                </Form.Select>
                            </div>
                            <div className="sp-meta mt-1" style={{ wordBreak: 'keep-all' }}>
                                {ticket.category === 'billing'
                                    ? '매장 파기 후에도 3년 보존된다 (분쟁 증거). 본문·제목의 휴대폰 번호는 파기 때 마스킹된다.'
                                    : '매장 파기 시 이 문의는 삭제된다. 결제 분쟁 기록이면 결제·구독으로 옮겨둔다.'}
                            </div>
                        </div>

                        {/* 가리기 — 답변하다가 손님 연락처를 발견하면 그 자리에서 끝낸다.
                            파기 때까지 미루면(현역 매장이면 수년) 보유 근거 없는 개인정보를
                            그만큼 더 들고 있는 것이 된다. 지우지 않고 가리는 이유는 위 핸들러 주석. */}
                        <div className="mb-3">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <span className="sp-meta text-nowrap">개인정보 가리기</span>
                                <Button size="sm" variant={confirmAuto ? 'danger' : 'outline-secondary'}
                                    disabled={saving} onClick={redactAuto} className="text-nowrap">
                                    {confirmAuto ? '되돌릴 수 없습니다 - 한 번 더' : '번호 자동 가리기'}
                                </Button>
                                {confirmAuto && (
                                    <Button size="sm" variant="link" disabled={saving}
                                        onClick={() => setConfirmAuto(false)} className="text-nowrap">
                                        취소
                                    </Button>
                                )}
                            </div>
                            {sel ? (
                                <div className="mt-2">
                                    <div className="sp-redact-sel">
                                        선택: <strong>{sel.text}</strong>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2 mt-1">
                                        <Button size="sm" variant="danger" disabled={saving}
                                            onClick={redactSelection} className="text-nowrap">
                                            이 부분 가리기 ({sel.end - sel.start}자)
                                        </Button>
                                        <Button size="sm" variant="link" disabled={saving}
                                            onClick={() => setSel(null)} className="text-nowrap">
                                            선택 해제
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="sp-meta mt-1" style={{ wordBreak: 'keep-all' }}>
                                    한글로 적힌 번호·이름처럼 자동이 못 잡는 건 본문에서 직접 드래그해 선택하세요.
                                </div>
                            )}
                        </div>
                        <Form onSubmit={handleReply}>
                            <Form.Control as="textarea" rows={4} value={reply} maxLength={4000}
                                placeholder="답변을 입력하세요"
                                onChange={e => setReply(e.target.value)} />
                            <div className="d-flex flex-wrap gap-2 mt-2">
                                <ButtonGroup size="sm">
                                    {STATUSES.map(s => (
                                        <Button key={s.value}
                                            variant={ticket.status === s.value ? 'secondary' : 'outline-secondary'}
                                            disabled={saving || ticket.status === s.value}
                                            onClick={() => changeStatus(s.value)}>
                                            {s.label}
                                        </Button>
                                    ))}
                                </ButtonGroup>
                                <Button type="submit" variant="primary" className="ms-auto text-nowrap"
                                    disabled={saving || !reply.trim()}>
                                    <FaPaperPlane className="me-1" />{saving ? '등록 중...' : '답변 등록'}
                                </Button>
                            </div>
                        </Form>
                    </Card.Footer>
                </Card>
            </Container>
        );
    }

    // ── 목록 ──
    const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
    return (
        <Container className="py-4">
            <h2 className="mb-3" style={{ fontSize: '1.5rem' }}>고객센터 문의</h2>

            <Row className="g-2 mb-3">
                <Col xs={6} sm={3}>
                    <Form.Select size="sm" value={filters.status}
                        onChange={e => { setPage(1); setFilters({ ...filters, status: e.target.value }); }}>
                        <option value="">상태 전체</option>
                        {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </Form.Select>
                </Col>
                <Col xs={6} sm={3}>
                    <Form.Select size="sm" value={filters.category}
                        onChange={e => { setPage(1); setFilters({ ...filters, category: e.target.value }); }}>
                        <option value="">분류 전체</option>
                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </Form.Select>
                </Col>
                <Col xs={12} sm={6} className="text-sm-end sp-meta d-flex align-items-center justify-content-sm-end">
                    총 {total}건
                </Col>
            </Row>

            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : items.length === 0 ? (
                <Card><Card.Body className="text-center text-muted py-5">문의가 없습니다.</Card.Body></Card>
            ) : (
                <Card>
                    <Table hover responsive className="mb-0 align-middle">
                        <thead>
                            <tr>
                                <th>등록</th>
                                <th>매장</th>
                                <th>분류</th>
                                <th>제목</th>
                                <th>상태</th>
                                <th className="text-end">최근</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(t => {
                                const cat = categoryMeta(t.category);
                                const st = statusMeta(t.status);
                                return (
                                    <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(t.id)}>
                                        <td className="sp-meta text-nowrap">{formatDateTime(t.created_at)}</td>
                                        <td className="text-nowrap">{t.shopName || `#${t.shop_id}`}</td>
                                        <td><Badge bg={cat.variant}>{cat.label}</Badge></td>
                                        <td>
                                            <span className="sp-list-title">{t.title}</span>
                                            {t.admin_unread ? <Badge bg="primary" className="ms-2">확인 필요</Badge> : null}
                                            <div className="sp-meta">글 {t.message_count}개</div>
                                        </td>
                                        <td><Badge bg={st.variant}>{st.label}</Badge></td>
                                        <td className="sp-meta text-end text-nowrap">
                                            {formatDateTime(t.last_reply_at || t.created_at)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Card>
            )}

            {lastPage > 1 && (
                <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                    <Button size="sm" variant="outline-secondary" disabled={page <= 1}
                        onClick={() => setPage(p => p - 1)}>이전</Button>
                    <span className="sp-meta">{page} / {lastPage}</span>
                    <Button size="sm" variant="outline-secondary" disabled={page >= lastPage}
                        onClick={() => setPage(p => p + 1)}>다음</Button>
                </div>
            )}
        </Container>
    );
};

export default SupportAdminPage;
